import React, { useState, useEffect, useMemo, useRef } from "react";
import BonusConfirmPopup from "./BonusConfirmPopup";
import RulesPopup from "./RulesPopup";
import { motion } from "framer-motion";
import config from "../config";
import { useStatic } from "../context/StaticContext";
import { useCss } from "../context/CssContext";
import { useTranslation } from "../hooks/useTranslation";
import { normalizeCat } from "../helper/categoryIcon";
import "../css/CleanMaintenanceMode.css";

const BonusCard = ({
  title,
  processName,
  imagePath,
  isMaintenance,
  rules,
  isMultiple,
  multipleOptions,
  redirectLink,
  confirmText,
  onMultipleBonusRequest,
  cardIndex = 0,
  bonusType = "",
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const [selectedBonusFromMultiple, setSelectedBonusFromMultiple] = useState(null);
  const { selectedBonus } = useStatic();
  const { cardLayoutMode, bonusCardGoldMode, cardBadgePosition = 'left' } = useCss();
  const { t } = useTranslation();
  const isTextOnly = cardLayoutMode === "text-only";

  // bonus_type "Tümü, Casino" gibi virgüllü string gelir. "Tümü" evrensel filtre
  // (hep ilk sırada) olduğu için rozet/ikon için ilk ANLAMLI kategoriyi seçiyoruz.
  const primaryCategory = useMemo(() => {
    if (!bonusType) return "";
    const parts = bonusType.split(",").map((s) => s.trim()).filter(Boolean);
    return parts.find((p) => normalizeCat(p) !== "tumu") || parts[0] || "";
  }, [bonusType]);

  const categories = useMemo(() => {
    if (!bonusType) return [];
    const parts = bonusType.split(",").map((s) => s.trim()).filter(Boolean);
    
    // Sadece deneme bonusu için çoklu gösterim (Casino ve Spor) geçerli olsun
    const isDeneme = (title && title.toLowerCase().includes("deneme")) || 
                     (processName && processName.toLowerCase().includes("deneme"));
                     
    if (isDeneme) {
      // Sadece "Casino" ve "Spor" kategorilerini filtrele
      const denemeCats = parts.filter((p) => {
        const norm = normalizeCat(p);
        return norm === "casino" || norm === "spor";
      });
      if (denemeCats.length > 0) return denemeCats;
    }
    
    // Diğer tüm kartlar veya denemeCats boşsa sadece tek bir birincil kategori göster
    const primary = parts.find((p) => normalizeCat(p) !== "tumu") || parts[0] || "";
    return primary ? [primary] : [];
  }, [bonusType, title, processName]);

  const titleRef = useRef(null);

  const handleRequestBonus = () => {
    if (isMaintenance) return;
    if (isMultiple && multipleOptions) {
      onMultipleBonusRequest(multipleOptions);
      return;
    }
    setIsConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setIsConfirmOpen(false);
    setSelectedBonusFromMultiple(null);
  };

  const handleConfirmBonus = () => {
    const bonusTitle = selectedBonusFromMultiple
      ? selectedBonusFromMultiple.bonus_name
      : title;
    alert(`${bonusTitle} talebi başarıyla onaylandı!`);
    setIsConfirmOpen(false);
    setSelectedBonusFromMultiple(null);
  };

  useEffect(() => {
    if (selectedBonus && selectedBonus.bonus && isMultiple && multipleOptions) {
      const isThisCard = multipleOptions.some(
        (option) => option.process_name === selectedBonus.bonus.process_name
      );
      if (isThisCard) {
        setSelectedBonusFromMultiple(selectedBonus.bonus);
        setIsConfirmOpen(true);
      }
    }
  }, [isMultiple, multipleOptions, selectedBonus]);

  const imageUrl = useMemo(() => {
    return imagePath && imagePath.startsWith("/")
      ? `${process.env.PUBLIC_URL}${imagePath}`
      : imagePath;
  }, [imagePath]);

  const bonusData = useMemo(() => {
    return selectedBonusFromMultiple
      ? {
        title: selectedBonusFromMultiple.bonus_name,
        process_name: selectedBonusFromMultiple.process_name,
        rules: selectedBonusFromMultiple.kurallar || rules,
        image: `${config.imgUrl}${imageUrl}`,
        redirect_link: selectedBonusFromMultiple.redirect_link || null,
        talep_text: selectedBonusFromMultiple.talep_text || confirmText || null,
        category: primaryCategory,
      }
      : {
        title: title,
        process_name: processName,
        rules: rules,
        image: `${config.imgUrl}${imageUrl}`,
        redirect_link: redirectLink || null,
        talep_text: confirmText || null,
        category: primaryCategory,
      };
  }, [selectedBonusFromMultiple, title, processName, rules, imageUrl, redirectLink, confirmText, primaryCategory]);

  const MaintenanceIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <>
      <motion.div
        className={`bonus-card-container ${isTextOnly ? "text-only" : ""} ${isMaintenance ? "clean-maintenance-mode" : ""} ${isTextOnly && cardIndex < 2 ? "top-square" : ""}`}
        initial={{ opacity: 0, y: 28, scale: 0.94 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -16, scale: 0.96 }}
        transition={{
          duration: 0.38,
          ease: [0.22, 1, 0.36, 1],
          delay: Math.min(cardIndex * 0.055, 0.40),
        }}
        layout
      >
        {isTextOnly ? (
          /* ── GÖRSELSİZ TASARIM ── */
          <div className={`bonus-card-textonly${isMaintenance ? " disabled" : ""}`}>
            {cardIndex < 2 ? (
              /* TOP SQUARE TASARIMI (İlk 2 kart) */
              <>
                <div className="textonly-square-top">
                  <div className="textonly-content">
                    <h3 ref={titleRef} className="textonly-title">{title}</h3>
                  </div>
                  {categories.length > 0 && (
                    <div className="textonly-badges-container square-top-badges">
                      {categories.map((cat, idx) => (
                        <span key={idx} className="textonly-badge" data-category={normalizeCat(cat)}>
                          {cat}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="textonly-square-bottom">
                  {isMaintenance ? (
                    <span className={`textonly-action disabled ${bonusCardGoldMode ? 'gold-theme' : ''}`}>
                      <MaintenanceIcon />
                      {t('maintenance.title', 'Bakımda')}
                    </span>
                  ) : (
                    <button className={`textonly-action ${bonusCardGoldMode ? 'gold-theme' : ''}`} onClick={handleRequestBonus}>
                      {t('buttons.request', 'TALEP ET')}
                    </button>
                  )}
                  {!isMaintenance && rules && rules.length > 0 && (
                    <button
                      className="textonly-info-btn-square"
                      onClick={(e) => { e.stopPropagation(); setIsRulesOpen(true); }}
                      title={t('buttons.rules', 'Kurallar')}
                    >
                      i
                    </button>
                  )}
                </div>
              </>
            ) : (
              /* YATAY LİSTE TASARIMI (Diğer kartlar) */
              <>
                {/* Info Butonu (Kurallar) */}
                {!isMaintenance && rules && rules.length > 0 && (
                  <button
                    className="textonly-info-btn"
                    onClick={(e) => { e.stopPropagation(); setIsRulesOpen(true); }}
                    title={t('buttons.rules', 'Kurallar')}
                  >
                    i
                  </button>
                )}

                {/* Kategori Rozeti */}
                {cardBadgePosition !== 'none' && categories.length > 0 && (
                  <div className="textonly-badges-container list-badges">
                    {categories.map((cat, idx) => (
                      <span
                        key={idx}
                        className="textonly-badge"
                        data-category={normalizeCat(cat)}
                      >
                        {cat}
                      </span>
                    ))}
                  </div>
                )}

                {/* Başlık ve Alt Başlık */}
                <div className="textonly-content">
                  <h3 ref={titleRef} className="textonly-title">{title}</h3>
                </div>

                {/* Talep Butonu */}
                {isMaintenance ? (
                  <span className={`textonly-action disabled ${bonusCardGoldMode ? 'gold-theme' : ''}`}>
                    <MaintenanceIcon />
                    {t('maintenance.title', 'Bakımda')}
                  </span>
                ) : (
                  <button
                    className={`textonly-action ${bonusCardGoldMode ? 'gold-theme' : ''}`}
                    onClick={handleRequestBonus}
                  >
                    {t('buttons.request', 'TALEP ET')}
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          /* ── VARSAYILAN: görselli kart ── */
          <div
            className="bonus-card"
            onClick={!isMaintenance ? handleRequestBonus : undefined}
            style={!isMaintenance ? { cursor: 'pointer' } : undefined}
          >
            <img
              src={`${config.imgUrl}${imageUrl}`}
              alt={title}
              className="card-image"
            />

            {/* Kategori Rozetleri (Sol Üst, Sağ Üst veya Gizli) */}
            {cardBadgePosition !== 'none' && categories.length > 0 && (
              <div className={`card-badges-container ${cardBadgePosition === 'right' ? 'badges-pos-right' : 'badges-pos-left'}`}>
                {categories.map((cat, idx) => (
                  <span 
                    key={idx}
                    className="card-badge-left"
                    data-category={normalizeCat(cat)}
                  >
                    {cat}
                  </span>
                ))}
              </div>
            )}

            {/* Info / Kurallar Butonu (Rozet sağdaysa info butonu sola geçer) */}
            {!isMaintenance && rules && rules.length > 0 && (
              <button
                className={`card-info-btn-right ${cardBadgePosition === 'right' ? 'info-pos-left' : ''}`}
                onClick={(e) => { e.stopPropagation(); setIsRulesOpen(true); }}
                title={t('buttons.rules', 'Kurallar')}
              >
                i
              </button>
            )}

            {/* Alt Bar Overlay: Başlık */}
            <div className={`card-bottom-overlay${isMaintenance ? " disabled" : ""}`}>
              <h3 className="card-overlay-title">{title}</h3>
            </div>

            {/* Bakım modu overlay */}
            {isMaintenance && (
              <div className="clean-maintenance-overlay">
                <div className="clean-maintenance-icon">
                  <MaintenanceIcon />
                </div>
                <p className="clean-maintenance-text">
                  {t('maintenance.title', 'Bakımda')}
                </p>
                <p className="clean-maintenance-subtitle">
                  {t('maintenance.subtitle', 'Yakında aktif olacak')}
                </p>
              </div>
            )}
          </div>
        )}
      </motion.div>

      <BonusConfirmPopup
        isOpen={isConfirmOpen}
        onClose={handleCloseConfirm}
        bonus={bonusData}
        onConfirm={handleConfirmBonus}
      />

      <RulesPopup
        isOpen={isRulesOpen}
        onClose={() => setIsRulesOpen(false)}
        bonusTitle={title}
        rules={rules}
        bonusImage={imageUrl ? `${config.imgUrl}${imageUrl}` : null}
        onRequest={!isMaintenance ? () => { setIsRulesOpen(false); setIsConfirmOpen(true); } : null}
      />
    </>
  );
};

export default BonusCard;
